import { useMemo, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { formatMoney, hoy } from '@abastece/shared';
import type { Deposito } from '@abastece/core';
import { db } from '../db/dexie.js';
import { rangoDe } from '../lib/historial.js';
import {
  MONEDAS_DEPOSITO,
  SIN_FILTROS_DEPOSITOS,
  VIGENCIAS,
  depositoCoincide,
  filtrosDeDepositos,
  ordenarDepositos,
  resumenDepositos,
  type FiltroDepositos,
  type MonedaDeposito,
  type Vigencia,
} from '../lib/historialDinero.js';
import { AnularDeposito } from '../ui/Correcciones.js';
import { enDolares } from '../ui/CuentasDeCaja.js';
import { FilaDeposito } from '../ui/FilasDeDinero.js';
import { Desplegable, FilaDesplegables, FiltroFechas, FiltrosEnLinea } from '../ui/FiltrosHistorial.js';
import { Ico } from '../ui/Iconos.js';
import { Pagina, Vacio } from '../ui/Pagina.js';
import { useT } from '../i18n/index.js';

/** How many show at a time: the latest, and «Ver más» for the rest. */
const LOTE = 50;

/**
 * «Depósitos», all of them: every time cash went to the bank, found by day, by currency and by
 * the slip's number. «Efectivo» keeps the latest ten, and voids one there as here.
 */
export function Depositos() {
  const t = useT();
  const hoyEs = hoy();
  // Cash brought back from the bank is kept with them, and listed in «Efectivo» on its own.
  const depositos = useLiveQuery(() => db.depositos.filter((d) => !d.delBanco).toArray(), [], undefined);
  const [filtro, setFiltro] = useState<FiltroDepositos>(SIN_FILTROS_DEPOSITOS);
  const [cuantos, setCuantos] = useState(LOTE);
  const [anulando, setAnulando] = useState<Deposito | null>(null);

  const rango = rangoDe(filtro.periodo, hoyEs, filtro);
  const puestos = filtrosDeDepositos(filtro, rango);
  const filtrando = filtro.texto.trim() !== '' || puestos.length > 0;
  const cambiar = (cambio: Partial<FiltroDepositos>) => {
    setFiltro((f) => ({ ...f, ...cambio }));
    setCuantos(LOTE);
  };
  const encontrados = useMemo(
    () => ordenarDepositos((depositos ?? []).filter((d) => depositoCoincide(d, filtro, rango))),
    // `rango` is worked out afresh each time: its two days are what it is.
    [depositos, filtro, rango.desde, rango.hasta],
  );
  const resumen = resumenDepositos(encontrados);
  const cuantosHay = depositos?.length ?? 0;
  const total = [
    resumen.cordobas > 0 || resumen.dolares === 0 ? formatMoney(resumen.cordobas) : null,
    resumen.dolares > 0 ? enDolares(resumen.dolares) : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <Pagina titulo={t('efectivo.depositos')} subtitulo={t('depositosHist.registrados', { n: cuantosHay })} volverA="/efectivo">
      {(filtrando || cuantosHay > 0) && (
        <FiltrosEnLinea
          buscar={{ texto: filtro.texto, onTexto: (texto) => cambiar({ texto }), placeholder: t('depositosHist.buscar') }}
          hayPuestos={puestos.length > 0}
          onQuitarTodos={() => cambiar({ ...SIN_FILTROS_DEPOSITOS, texto: filtro.texto })}
        >
          <FiltroFechas periodo={filtro.periodo} desde={filtro.desde} hasta={filtro.hasta} onCambio={cambiar} />
          <FilaDesplegables>
            <Desplegable
              clave="moneda"
              etiqueta={t('depositosHist.moneda')}
              value={filtro.moneda}
              onChange={(v) => cambiar({ moneda: v as MonedaDeposito })}
              elegido={filtro.moneda === 'todas' ? undefined : t(`depositosHist.moneda.${filtro.moneda}`)}
            >
              {MONEDAS_DEPOSITO.map((m) => (
                <option key={m} value={m}>
                  {t(`depositosHist.moneda.${m}`)}
                </option>
              ))}
            </Desplegable>
            <Desplegable
              clave="estado"
              etiqueta={t('historial.estado')}
              value={filtro.estado}
              onChange={(v) => cambiar({ estado: v as Vigencia })}
              elegido={filtro.estado === 'todos' ? undefined : t(`historial.vigencia.${filtro.estado}`)}
            >
              {VIGENCIAS.map((v) => (
                <option key={v} value={v}>
                  {t(`historial.vigencia.${v}`)}
                </option>
              ))}
            </Desplegable>
          </FilaDesplegables>
        </FiltrosEnLinea>
      )}

      {filtrando && encontrados.length > 0 && (
        <p className="tabular mb-3 text-sm text-slate-400" data-historial-resumen>
          {t('historial.encontrados', { n: resumen.n, total })}
        </p>
      )}

      {/* Nothing said while it is read: «nothing matches» was only «not read yet». */}
      {depositos === undefined ? null : encontrados.length === 0 ? (
        filtrando ? (
          <Vacio mensaje={t('depositosHist.ninguno')} icono="buscar">
            <button type="button" className="btn-secundario" onClick={() => cambiar(SIN_FILTROS_DEPOSITOS)}>
              <Ico icono="reiniciar" />
              {t('historial.quitarBusqueda')}
            </button>
          </Vacio>
        ) : (
          <Vacio mensaje={t('depositosHist.vacio')} icono="transferencias" />
        )
      ) : (
        <ul className="tarjeta flex flex-col divide-y divide-slate-800 py-1" data-historial-lista>
          {encontrados.slice(0, cuantos).map((d) => (
            <FilaDeposito key={d.id} deposito={d} onAnular={() => setAnulando(d)} />
          ))}
        </ul>
      )}
      {encontrados.length > cuantos && (
        <button type="button" className="btn-secundario mt-4 w-full" onClick={() => setCuantos((n) => n + LOTE)}>
          <Ico icono="desplegar" />
          {t('comun.verMas')}
        </button>
      )}

      {anulando && <AnularDeposito deposito={anulando} onCerrar={() => setAnulando(null)} />}
    </Pagina>
  );
}
